export const TRANSITION_PERMISSION_TYPES = {
    ALLOW: 'ALLOW',
    DENY: 'DENY',
};

interface TransitionPermission {
    rolePermissionId: string;
    transitionPermissionTypeId: string;
}

interface Transition {
    permissions?: TransitionPermission[];
}

/**
 * Deny-wins evaluation of a transition's WorkflowTransitionPermission rows
 * against the current user's RolePermission codes on the ticket's structural
 * node. Mirrors the API's authoritative check in
 * services/api/server/utils/workflowTransitionPermissions.ts — this is UI
 * filtering only; the transition endpoint enforces with a 403.
 *
 * A transition with no permission rows is unrestricted. While the user's
 * node permissions are still loading (not yet an array), gated transitions
 * fail closed.
 */
export function isTransitionPermitted(
    transition: Transition,
    userPermissionCodes?: string[],
): boolean {
    const permissionRows = transition.permissions || [];
    if (permissionRows.length === 0) return true;
    if (!Array.isArray(userPermissionCodes)) return false;

    const codes = new Set(userPermissionCodes);
    const allowRows = permissionRows.filter(
        (row) =>
            row.transitionPermissionTypeId ===
            TRANSITION_PERMISSION_TYPES.ALLOW,
    );
    const denyRows = permissionRows.filter(
        (row) =>
            row.transitionPermissionTypeId === TRANSITION_PERMISSION_TYPES.DENY,
    );

    if (denyRows.some((row) => codes.has(row.rolePermissionId))) return false;
    if (allowRows.length === 0) return true;
    return allowRows.some((row) => codes.has(row.rolePermissionId));
}

export default isTransitionPermitted;
